import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ServerCrash, UserRoundMinus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useModalProvider } from '@/common/hooks/useModalProvider'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function deleteUser(userId: string) {
  await api.backend.delete(`/user/delete-user/${userId}`)
}

export function useDeleteUser() {
  const { t } = useTranslation('registers')
  const { handleModal, toggleLoading } = useModalProvider()

  const { mutateAsync } = useMutation<unknown, AxiosError<ApiError>, string>({
    mutationKey: ['/user/id/delete'],
    mutationFn: deleteUser,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: UserRoundMinus,
          title: t('users.delete_user.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/user/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('users.delete_user.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('users.delete_user.toast_error.description'),
        },
      })
    },
  })

  const handleDeleteUser = (userId: string) => {
    handleModal({
      type: 'danger',
      title: t('users.delete_user.modal.title'),
      description: t('users.delete_user.modal.description'),
      confirmText: t('users.delete_user.modal.button_confirm'),
      onConfirm: async () => {
        toggleLoading()
        await mutateAsync(userId).finally(() => {
          toggleLoading()
        })
      },
    })
  }

  return {
    handleDeleteUser,
  }
}
